import { defaultCacheableSingleton } from '@/lib/cacheable';
import {
  Discipline,
  JobBlockDisplayType,
  Page,
  RowrowComponentsUnion,
} from '@/lib/types';

import JobCategoryPageDisciplinesService from '@/features/job-category-page/services/disciplines.service';
import lookerQueryClient from '@/pages/api/api-clients/looker-query-api-client';
import { endOfDay } from '@/utils/date-utils';
import { getPageComponents } from '@/utils/page-static-utils';

type LookerPopularLocation = {
  state: string;
  city: string;
  location_api_id: string;
  specialty: string;
  discipline: string;
  weekly_pay: number;
  hygraphDiscipline: Discipline | null;
};

type TPopularLocation = LookerPopularLocation & {
  hygraphDiscipline: Discipline | null;
};

export type PopularLocationsResponse = TPopularLocation[];

const getPopularLocations = async (): Promise<LookerPopularLocation[]> => {
  return defaultCacheableSingleton.remember(
    'looker:popularLocations',
    async () => {
      return lookerQueryClient
        .get('api/v1/looker/popular-locations')
        .then((response) => response.data.data)
        .then((data) =>
          data.filter((item: { state: string }) => {
            // Filtering out DC as it's not a state within Nomad's system
            // Therefore we cant send the user to the proper seo page/search filter
            if (item.state === 'DC') {
              return false;
            }

            return true;
          })
        );
    },
    endOfDay(new Date())
  );
};

const get = async (page: Page): Promise<PopularLocationsResponse> => {
  const hasPopularLocationsCheck = (rc: RowrowComponentsUnion) =>
    rc.__typename === 'JobBlock' &&
    rc.jobBlockDisplayType === JobBlockDisplayType.Locations;
  const hasPopularLocations =
    getPageComponents(page, hasPopularLocationsCheck).length > 0;

  if (!hasPopularLocations) {
    return [];
  }

  const lookerPopularLocations = await getPopularLocations();
  const hygraphDisciplines = await JobCategoryPageDisciplinesService.getAll();

  // There is a mismatch between the Nomad discipline and the hygraph discipline slug.
  // Since looker comes from Nomad data, we need to map the discipline to the hygraph discipline,
  // so that we can build the correct SEO url for the location
  // i.e. it is `lab-tech` in hygraph, but `lab-technician` in looker
  const popularLocationsWithDiscipline = lookerPopularLocations.reduce<
    TPopularLocation[]
  >((acc, lookerLocation) => {
    const hygraphDiscipline = hygraphDisciplines.find(
      (hygraphDiscipline) =>
        hygraphDiscipline.apiId === lookerLocation.discipline
    );

    if (!hygraphDiscipline) {
      return acc;
    }

    return [
      ...acc,
      {
        ...lookerLocation,
        hygraphDiscipline,
      },
    ];
  }, []);

  return popularLocationsWithDiscipline;
};

const PopularLocationsService = {
  get,
};

export default PopularLocationsService;
